import type { ColumnsType } from 'antd/es/table';
import type { GenderData, DateRange, ShowFilter, TableData, DownloadConfig } from '@/types';
import type { GenderPlaysResponse } from '@/types/api/gender';
import { uppercaseFirstLetter } from '@/utils';
import { DownloadOutlined, RightOutlined } from '@ant-design/icons';
import ModalEcho from '@/components/modals/Modal/ModalEcho';
import { useGenderPlaysQuery } from '@/services/revenueApi';
import { useDerivedNetworkContext } from '@/context';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useGenderCsvExport from './useGenderCsvExport';
import DataAvailabilityBanner from './DataAvailabilityBanner';
import useIsBeforeDataAvailability from './useIsBeforeDataAvailability';
import {
  trackAudienceGenderModalDownloadClicked,
  trackAudienceGenderModalOpened,
} from './audienceModalMixpanel';
import { Button, Flex, theme } from 'antd';
import LoaderEcho from '@/components/LoaderEcho';
import DataTableEcho from '@/components/tables/DataTable/DataTableEcho';
import NoDataMessageEcho from '@/components/states/NoDataMessageEcho';

type GenderRow = TableData & {
  key: string;
  gender: string;
  [platform: string]: unknown;
};

const buildGenderTableData = (plays: GenderPlaysResponse['plays']): GenderRow[] => {
  const genderMap: Record<string, Record<string, number>> = {};

  plays.forEach(({ platform, gender_distribution }) => {
    gender_distribution.forEach(({ label, percentage }: GenderData) => {
      if (!genderMap[label]) genderMap[label] = {};
      genderMap[label][platform] = percentage;
    });
  });

  return Object.entries(genderMap).map(([gender, platformValues]) => ({
    id: gender,
    columns: [],
    key: gender,
    gender,
    ...platformValues,
  }));
};

const buildGenderTableColumns = (plays: GenderPlaysResponse['plays']): ColumnsType<TableData> => [
  {
    key: 'gender',
    title: '',
    render: (_value, row) => uppercaseFirstLetter((row as GenderRow).gender),
  },
  ...plays.map(({ platform }) => ({
    key: platform,
    title: uppercaseFirstLetter(platform.replace(/_/g, ' ')),
    render: (_value: unknown, row: TableData) => {
      const percentage = (row as GenderRow)[platform];
      return percentage != null ? `${percentage}%` : '—';
    },
    align: 'right' as const,
  })),
];

const GenderModalContent = ({
  dateRange,
  selectedShow,
}: {
  dateRange: DateRange;
  selectedShow: ShowFilter;
}) => {
  const { token } = theme.useToken();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { audienceSources } = useFlags<{ audienceSources?: string[] }>();
  const sources = audienceSources ?? ['acast', 'phyllo'];
  const showIds = selectedShow !== 'all' && selectedShow ? [selectedShow] : undefined;

  const { data, isPending } = useGenderPlaysQuery(
    selectedNetwork.id,
    dateRange.from,
    dateRange.to,
    undefined,
    { showIds, sources }
  );

  const isBeforeDataAvailability = useIsBeforeDataAvailability('gender', dateRange.from);

  if (isBeforeDataAvailability) {
    return <DataAvailabilityBanner dataType="gender" />;
  }

  if (isPending) {
    return (
      <Flex justify="center" align="center" style={{ height: '60vh' }}>
        <LoaderEcho data-testid="loader" />
      </Flex>
    );
  }

  if (!data || data.plays.length < 1) {
    return <NoDataMessageEcho dataType="gender data" />;
  }

  const tableData = buildGenderTableData(data.plays);
  const columns = buildGenderTableColumns(data.plays);

  return (
    <Flex vertical gap={token.margin} style={{ padding: token.padding }}>
      <DataTableEcho columns={columns} dataSource={tableData} pagination={false} rowKey="key" />
    </Flex>
  );
};

const GenderModalEcho = () => {
  const { handleCSVExport } = useGenderCsvExport();

  return (
    <ModalEcho
      hashProp="gender-details"
      title="Gender details"
      showDateRangeFilter
      onOpen={trackAudienceGenderModalOpened}
      downloadConfig={{
        onDownload: (...args) => {
          trackAudienceGenderModalDownloadClicked();
          return handleCSVExport(...args);
        },
        downloadButtonText: 'Export as CSV',
        downloadButtonIcon: DownloadOutlined as DownloadConfig['downloadButtonIcon'],
      }}
      renderTrigger={(openModal) => (
        <Flex justify="flex-end" style={{ alignSelf: 'flex-end' }}>
          <Button
            type="text"
            color="primary"
            variant="text"
            icon={<RightOutlined />}
            iconPlacement="end"
            onClick={openModal}
          >
            More details
          </Button>
        </Flex>
      )}
    >
      {(dateRange, selectedShow) => (
        <GenderModalContent dateRange={dateRange} selectedShow={selectedShow} />
      )}
    </ModalEcho>
  );
};

export default GenderModalEcho;
